import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { OrderActionsDialogComponent } from '../../order-actions-dialog/order-actions-dialog.component';
import { AlertApiClientService } from '../../services/alert-api-client.service';
import { AlertGridOptionsService } from '../alert-column-definition/alert-grid-options.service';

@Component({
  selector: 'app-watch-order-action-renderer',
  templateUrl: './watch-order-action-renderer.component.html',
  styleUrls: ['./watch-order-action-renderer.component.scss'],
  standalone: false,
})
export class WatchOrderActionRendererComponent
  implements ICellRendererAngularComp
{
  private dialog = inject(MatDialog);
  private snackbarService = inject(SnackbarService);
  private alertApiClientService = inject(AlertApiClientService);
  private alertGridOptionsService = inject(AlertGridOptionsService);

  public params!: ICellRendererParams;
  public clientReference!: string[];
  public subClientReference!: string[];
  public canDelete = false;
  public canMove = false;
  public isSystemGenerated: boolean | undefined;

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.findClientAndSubClientReferences();
    this.canDelete = this.canDeleteOrder();
    this.canMove = this.canMoveOrder();
    this.isSystemGenerated = this.isSystemGeneratedAlert();
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    return true;
  }

  private getCellParams(): any {
    return (this.params as any).colDef?.cellRendererParams?.() ?? {};
  }

  private get parentGridApi(): any {
    return this.getCellParams().parentGridApi;
  }

  private get currentAlertId(): any {
    return this.getCellParams().currentAlertId;
  }

  private getCurrentAlertData(): any {
    if (!this.parentGridApi) {
      return;
    }
    let found: any;
    this.parentGridApi.forEachNodeAfterFilterAndSort((node: any) => {
      const data = node.data;
      if (data && !data._panelRow) {
        const id = data.id || data.alertId || data.alert_id;
        if (id === this.currentAlertId) {
          found = data;
        }
      }
    });
    return found;
  }

  private getFilteredAlertCount(): number {
    let count = 0;
    this.parentGridApi.forEachNodeAfterFilterAndSort((node: any) => {
      if (!node.id?.includes('__panel')) {
        count++;
      }
    });
    return count;
  }

  onCopyOrder(event: Event): void {
    this.openOrderActionsDialog('copy', event);
  }

  onMoveOrder(event: Event): void {
    this.openOrderActionsDialog('move', event);
  }

  private openOrderActionsDialog(action: 'copy' | 'move', event: Event): void {
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    const watchAlerts = this.getWatchAlertIdandTitles(
      this.parentGridApi,
      this.currentAlertId,
    );

    const dialogRef = this.dialog.open(OrderActionsDialogComponent, {
      width: '16rem',
      data: {
        watchAlerts,
        orderNumber: this.params.data.id,
        sourceAlertId: this.currentAlertId,
        action,
        params: this.params,
        parentGridApi: this.parentGridApi,
      },
      position: {
        top: `${rect.bottom + 8}px`,
        left: `${rect.left}px`,
      },
      backdropClass: 'cdk-overlay-transparent-backdrop',
      hasBackdrop: true,
      panelClass: 'order-actions-dialog-panel',
    });

    dialogRef.afterClosed().subscribe();
  }

  private getWatchAlertIdandTitles(
    parentGridApi: any,
    currentAlertId: string,
  ): {
    id: string;
    title: string;
  }[] {
    return this.alertGridOptionsService.getFilteredWatchAlerts(
      parentGridApi,
      currentAlertId,
    );
  }

  removeWatchOrder(): void {
    const orderNumber = this.params.data.id;
    if (!orderNumber) {
      return;
    }
    const alertData = this.getCurrentAlertData();
    const alertTitle =
      alertData?.title || alertData?.alertTitle || alertData?.name;
    const isLastOrder = alertData?.orders === 1;
    const shoudClearFilters = this.getFilteredAlertCount() === 1;

    let visibleRowsBeforeRemove = 0;
    this.params.api.forEachNodeAfterFilterAndSort(() => {
      visibleRowsBeforeRemove++;
    });

    this.alertApiClientService
      .deleteWatchOrder(this.currentAlertId, orderNumber)
      .subscribe({
        next: () => {
          this.snackbarService.openHtmlSnackBar(
            [
              { text: `Removed ` },
              { text: '1', bold: true },
              { text: ` order from "${alertTitle}"` },
            ],
            'Dismiss',
          );

          this.params.api.applyTransaction({ remove: [this.params.data] });

          if (visibleRowsBeforeRemove === 1) {
            const allCurrentRows: any[] = [];
            this.params.api.forEachNode((node: any) => {
              if (node.data) {
                allCurrentRows.push(node.data);
              }
            });

            if (allCurrentRows.length > 0) {
              this.params.api.setFilterModel(null);
              this.params.api.setGridOption('rowData', allCurrentRows);
            }
          }

          if (isLastOrder && shoudClearFilters) {
            this.alertGridOptionsService.clearAlertFilters();
          }
          this.alertGridOptionsService.refreshGrid();
        },
      });
  }
  private findClientAndSubClientReferences(): void {
    const alertData = this.getCurrentAlertData();
    if (!alertData) {
      return;
    }
    this.clientReference = [...(alertData.clientReference ?? [])];
    this.subClientReference = [...(alertData.subClientReference ?? [])];
  }

  private isSystemGeneratedAlert(): boolean {
    let isSystemAlert = false;
    this.parentGridApi.forEachNodeAfterFilterAndSort((node: any) => {
      const id = node.data.alertId || node.data.id || node.data.alert_id;
      if (this.currentAlertId && String(this.currentAlertId) === String(id)) {
        isSystemAlert = node.data?.systemGenerated;
      }
    });
    return isSystemAlert;
  }
  private canDeleteOrder(): boolean {
    return !this.doesAnyReferenceMatch() && !this.isSystemGeneratedAlert();
  }

  private canMoveOrder(): boolean {
    return !this.doesAnyReferenceMatch();
  }

  private doesAnyReferenceMatch(): boolean {
    const orderClientReference = this.params.data.clientReference;
    const orderSubClientReference = this.params.data.subClientReference;
    const clientReferenceMatch =
      orderClientReference &&
      this.clientReference.includes(orderClientReference);
    const subClientReferenceMatch =
      orderSubClientReference &&
      this.subClientReference.includes(orderSubClientReference);
    return clientReferenceMatch || subClientReferenceMatch;
  }
}
